import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Plus, Pencil, Trash2, Users, Lock, Crown, Phone, Mail, Award, CheckCircle, XCircle, Camera, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const certificationLevels = [
  { value: 'standard', label: 'Standard', color: 'bg-gray-500' },
  { value: 'advanced', label: 'Advanced', color: 'bg-blue-500' },
  { value: 'professional', label: 'Professional', color: 'bg-emerald-500' }
];

export default function RefereesPage() {
  const { token, organization } = useAuth();
  const navigate = useNavigate();
  const [referees, setReferees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReferee, setEditingReferee] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [refereeToDelete, setRefereeToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    certification_level: 'standard',
    joined_date: '',
    bio: ''
  });
  
  // Photo upload states
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const canManageReferees = organization?.plan === 'pro' || organization?.plan === 'enterprise';

  useEffect(() => {
    if (organization) {
      fetchReferees();
    }
  }, [organization]);

  const fetchReferees = async () => {
    if (!canManageReferees) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/referees/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReferees(data);
      }
    } catch (error) {
      console.error('Failed to fetch referees:', error);
    } finally {
      setLoading(false);
    }
  };

  // Photo handling functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image must be less than 2MB');
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error('Only JPG, PNG, and WebP images are allowed');
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 640, height: 480 } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setShowCamera(true);
    } catch (error) {
      toast.error('Could not access camera. Please check permissions.');
      console.error('Camera error:', error);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
        setPhotoFile(file);
        setPhotoPreview(canvas.toDataURL('image/jpeg'));
        stopCamera();
      }, 'image/jpeg', 0.8);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadPhoto = async (refereeId) => {
    if (!photoFile) return;
    
    setUploadingPhoto(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', photoFile);
      
      const response = await fetch(`${API_URL}/api/referees/${refereeId}/photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      
      if (response.ok) {
        toast.success('Photo uploaded successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload photo');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingReferee) {
        const response = await fetch(`${API_URL}/api/referees/${editingReferee.referee_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          // Upload photo if selected
          if (photoFile) {
            await uploadPhoto(editingReferee.referee_id);
          }
          toast.success('Referee updated successfully');
          fetchReferees();
          resetForm();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to update referee');
        }
      } else {
        const response = await fetch(`${API_URL}/api/referees`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            ...formData,
            org_id: organization.org_id
          })
        });
        
        if (response.ok) {
          const newReferee = await response.json();
          // Upload photo if selected
          if (photoFile) {
            await uploadPhoto(newReferee.referee_id);
          }
          toast.success('Referee added successfully');
          fetchReferees();
          resetForm();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Failed to add referee');
        }
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const handleEdit = (referee) => {
    setEditingReferee(referee);
    setFormData({
      name: referee.name,
      email: referee.email || '',
      phone: referee.phone || '',
      certification_level: referee.certification_level,
      joined_date: referee.joined_date || '',
      bio: referee.bio || ''
    });
    // Set existing photo preview if available
    if (referee.photo) {
      setPhotoPreview(referee.photo);
    }
    setDialogOpen(true);
  };

  const handleDeleteClick = (referee) => {
    setRefereeToDelete(referee);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!refereeToDelete) return;
    
    try {
      const response = await fetch(`${API_URL}/api/referees/${refereeToDelete.referee_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Referee deleted successfully');
        fetchReferees();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete referee');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setDeleteConfirmOpen(false);
      setRefereeToDelete(null);
    }
  };

  const toggleRefereeStatus = async (referee) => {
    try {
      const response = await fetch(`${API_URL}/api/referees/${referee.referee_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !referee.is_active })
      });
      
      if (response.ok) {
        toast.success(`Referee ${referee.is_active ? 'deactivated' : 'activated'}`);
        fetchReferees();
      }
    } catch (error) {
      toast.error('Failed to update referee status');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setEditingReferee(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      certification_level: 'standard',
      joined_date: '',
      bio: ''
    });
    // Clear photo states
    setPhotoFile(null);
    setPhotoPreview(null);
    stopCamera();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getExperienceText = (experience) => {
    if (!experience) return 'New';
    const { years, months } = experience;
    if (years === 0 && months === 0) return 'New';
    if (years === 0) return `${months} month${months !== 1 ? 's' : ''}`;
    if (months === 0) return `${years} year${years !== 1 ? 's' : ''}`;
    return `${years}y ${months}m`;
  };

  const getCertificationBadge = (level) => {
    const cert = certificationLevels.find(c => c.value === level);
    return cert || certificationLevels[0];
  };

  // Plan gate - show upgrade prompt for non-Pro/Enterprise users
  if (!canManageReferees) {
    return (
      <DashboardLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Card className="max-w-lg bg-[#1a1a1b] border-gray-200">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="text-emerald-400" size={32} />
              </div>
              <h2 className="text-2xl font-heading font-bold text-black uppercase mb-2">
                Referee Management
              </h2>
              <p className="text-gray-600 mb-6">
                Add and manage match officials, assign referees to games, and track their availability.
                This feature is available on Pro and Enterprise plans.
              </p>
              <div className="flex items-center justify-center gap-2 mb-6">
                <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium">
                  Pro
                </span>
                <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm font-medium">
                  Enterprise
                </span>
              </div>
              <Button 
                onClick={() => navigate('/billing')}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                <Crown size={18} className="mr-2" />
                Upgrade to Pro
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-emerald-500">Loading referees...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-black uppercase">Match Officials</h1>
            <p className="text-gray-600 text-sm mt-1">Manage your referees and match officials</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-emerald-500 hover:bg-emerald-600"
                onClick={() => resetForm()}
                data-testid="add-referee-btn"
              >
                <Plus size={18} className="mr-2" />
                Add Referee
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a1a1b] border-gray-200">
              <DialogHeader>
                <DialogTitle className="text-black font-heading uppercase">
                  {editingReferee ? 'Edit Referee' : 'Add New Referee'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Enter referee name"
                    required
                    className="bg-gray-100 border-gray-200 text-black"
                    data-testid="referee-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-700">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="referee@example.com"
                    className="bg-gray-100 border-gray-200 text-black"
                    data-testid="referee-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-700">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="+1 234 567 8900"
                    className="bg-gray-100 border-gray-200 text-black"
                    data-testid="referee-phone-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-700">Certification Level</Label>
                  <Select 
                    value={formData.certification_level}
                    onValueChange={(value) => setFormData({...formData, certification_level: value})}
                  >
                    <SelectTrigger className="bg-gray-100 border-gray-200 text-black" data-testid="referee-cert-select">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1b] border-gray-200">
                      {certificationLevels.map(level => (
                        <SelectItem 
                          key={level.value} 
                          value={level.value}
                          className="text-black hover:bg-gray-100"
                        >
                          <span className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${level.color}`}></span>
                            {level.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Photo Upload Section */}
                <div className="space-y-2">
                  <Label className="text-gray-700">Photo</Label>
                  
                  {/* Camera View */}
                  {showCamera && (
                    <div className="relative bg-black rounded-lg overflow-hidden">
                      <video 
                        ref={videoRef} 
                        autoPlay 
                        playsInline 
                        className="w-full h-48 object-cover"
                      />
                      <canvas ref={canvasRef} className="hidden" />
                      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
                        <Button
                          type="button"
                          onClick={capturePhoto}
                          className="bg-emerald-500 hover:bg-emerald-600"
                        >
                          <Camera size={16} className="mr-1" />
                          Capture
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={stopCamera}
                          className="text-black bg-black/50"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Photo Preview */}
                  {!showCamera && photoPreview && (
                    <div className="relative w-24 h-24 mx-auto">
                      <img 
                        src={photoPreview} 
                        alt="Preview" 
                        className="w-24 h-24 rounded-full object-cover border-2 border-emerald-500/30"
                      />
                      <button
                        type="button"
                        onClick={clearPhoto}
                        className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-black hover:bg-red-600"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                  
                  {/* Upload Buttons */}
                  {!showCamera && !photoPreview && (
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex-1 border-gray-200 text-gray-700 hover:text-black"
                      >
                        <Upload size={16} className="mr-2" />
                        Upload Photo
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={startCamera}
                        className="flex-1 border-gray-200 text-gray-700 hover:text-black"
                      >
                        <Camera size={16} className="mr-2" />
                        Take Selfie
                      </Button>
                    </div>
                  )}
                  
                  {!showCamera && photoPreview && !photoFile && (
                    <div className="flex gap-2 mt-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex-1 border-gray-200 text-gray-700 hover:text-black"
                      >
                        <Upload size={14} className="mr-1" />
                        Change
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={startCamera}
                        className="flex-1 border-gray-200 text-gray-700 hover:text-black"
                      >
                        <Camera size={14} className="mr-1" />
                        Retake
                      </Button>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500">Max 2MB. JPG, PNG, or WebP</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Date Joined League</Label>
                  <Input
                    type="date"
                    value={formData.joined_date}
                    onChange={(e) => setFormData({...formData, joined_date: e.target.value})}
                    className="bg-gray-100 border-gray-200 text-black"
                    data-testid="referee-joined-input"
                  />
                  {formData.joined_date && (
                    <p className="text-xs text-emerald-400">
                      Experience: {getExperienceText({
                        years: Math.floor((new Date() - new Date(formData.joined_date)) / (365.25 * 24 * 60 * 60 * 1000)),
                        months: Math.floor(((new Date() - new Date(formData.joined_date)) % (365.25 * 24 * 60 * 60 * 1000)) / (30 * 24 * 60 * 60 * 1000))
                      })}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Apart from match officiating</Label>
                  <Input
                    value={formData.bio}
                    onChange={(e) => setFormData({...formData, bio: e.target.value})}
                    placeholder="A one-liner about their life..."
                    maxLength={150}
                    className="bg-gray-100 border-gray-200 text-black"
                    data-testid="referee-bio-input"
                  />
                  <p className="text-xs text-gray-500">{formData.bio?.length || 0}/150 characters</p>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="ghost" 
                    onClick={resetForm}
                    className="flex-1 text-gray-600"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                    data-testid="save-referee-btn"
                  >
                    {editingReferee ? 'Update' : 'Add Referee'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#1a1a1b] border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Users className="text-emerald-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{referees.length}</p>
                  <p className="text-xs text-gray-600">Total Referees</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a1a1b] border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="text-green-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{referees.filter(r => r.is_active).length}</p>
                  <p className="text-xs text-gray-600">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a1a1b] border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Award className="text-emerald-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">
                    {referees.filter(r => r.certification_level === 'professional').length}
                  </p>
                  <p className="text-xs text-gray-600">Professional</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a1a1b] border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Award className="text-blue-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">
                    {referees.reduce((acc, r) => acc + (r.matches_officiated || 0), 0)}
                  </p>
                  <p className="text-xs text-gray-600">Matches Officiated</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Referees List */}
        {referees.length === 0 ? (
          <Card className="bg-[#1a1a1b] border-gray-200">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-emerald-400" size={32} />
              </div>
              <h3 className="text-lg font-heading font-bold text-black uppercase mb-2">No Referees Yet</h3>
              <p className="text-gray-600 mb-4">Add your first referee to start managing match officials.</p>
              <Button 
                onClick={() => setDialogOpen(true)}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                <Plus size={18} className="mr-2" />
                Add First Referee
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {referees.map((referee) => {
              const certBadge = getCertificationBadge(referee.certification_level);
              return (
                <Card 
                  key={referee.referee_id} 
                  className={`bg-[#1a1a1b] border-gray-200 ${!referee.is_active ? 'opacity-60' : ''} cursor-pointer hover:border-emerald-500/30 transition-colors`}
                  data-testid={`referee-card-${referee.referee_id}`}
                  onClick={() => navigate(`/referee/${referee.referee_id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {referee.photo ? (
                          <img 
                            src={referee.photo} 
                            alt={referee.name}
                            className="w-14 h-14 rounded-full object-cover border-2 border-emerald-500/30"
                          />
                        ) : (
                          <div className="w-14 h-14 bg-emerald-500/20 rounded-full flex items-center justify-center border-2 border-emerald-500/30">
                            <span className="text-emerald-400 text-xl font-bold">
                              {referee.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <h3 className="text-black font-medium">{referee.name}</h3>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${certBadge.color} text-black`}>
                            {certBadge.label}
                          </span>
                          {referee.experience && (
                            <p className="text-xs text-gray-600 mt-1">
                              {getExperienceText(referee.experience)} experience
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEdit(referee)}
                          className="text-gray-600 hover:text-black"
                          data-testid={`edit-referee-${referee.referee_id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteClick(referee)}
                          className="text-gray-600 hover:text-red-400"
                          data-testid={`delete-referee-${referee.referee_id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Rating display */}
                    {referee.rating && referee.rating.total_votes > 0 && (
                      <div className="flex items-center gap-2 mb-3 p-2 bg-gray-100 rounded-lg">
                        <span className={`text-lg ${referee.rating.percentage >= 70 ? 'text-green-400' : referee.rating.percentage >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                          {referee.rating.percentage}%
                        </span>
                        <span className="text-xs text-gray-600">
                          positive ({referee.rating.total_votes} votes)
                        </span>
                      </div>
                    )}
                    
                    {/* Bio */}
                    {referee.bio && (
                      <p className="text-gray-600 text-sm italic mb-3 line-clamp-2">
                        "{referee.bio}"
                      </p>
                    )}
                    
                    <div className="space-y-2 text-sm">
                      {referee.email && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Mail size={14} />
                          <span>{referee.email}</span>
                        </div>
                      )}
                      {referee.phone && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone size={14} />
                          <span>{referee.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-600">
                        <Award size={14} />
                        <span>{referee.matches_officiated || 0} matches officiated</span>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-3 border-t border-gray-200 flex items-center justify-between">
                      <span className={`text-xs ${referee.is_active ? 'text-green-400' : 'text-gray-500'}`}>
                        {referee.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleRefereeStatus(referee)}
                        className={referee.is_active ? 'text-gray-600 hover:text-red-400' : 'text-gray-600 hover:text-green-400'}
                      >
                        {referee.is_active ? (
                          <>
                            <XCircle size={14} className="mr-1" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <CheckCircle size={14} className="mr-1" />
                            Activate
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-[#1a1a1b] border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-black font-heading uppercase">Delete Referee</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">
              Are you sure you want to delete <strong className="text-black">{refereeToDelete?.name}</strong>?
            </p>
            <p className="text-gray-500 text-sm mt-2">
              This will also remove them from any assigned matches.
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="ghost" 
              onClick={() => setDeleteConfirmOpen(false)}
              className="flex-1 text-gray-600"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDelete}
              className="flex-1 bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-referee-btn"
            >
              <Trash2 size={16} className="mr-2" />
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
